import {
  SignatureRequest,
  SignatureRequestStatus
} from "@wingspanhq/wingspan-api-sdk";

import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import { useDownloadVaultFile } from "../../../../hooks/useDownloadVaultFile";
import { selectorIsRequirementReadyToSignAsPayee } from "../../../../selectors/selectorIsRequirementReadyToSignAsPayee";
import {
  CellsConfig,
  ContextItem,
  RequirementAction,
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../../types";
import { SignaturesFieldsWidget } from "../../components/SignaturesFieldsWidget";
import { useSignSignatureRequest } from "../../hooks/useSignSignatureRequest";
import { getSignaturePayeeRequirementStatus } from "../helpers/getSignaturePayeeRequirementStatus";
import { getSignatureRequestProgressSteps } from "../helpers/getSignatureRequestProgressSteps";
import { getSignatureRequirementName } from "../helpers/getSignatureRequirementName";
import { getHasPayerSigner } from "../helpers/getSignerByRole";
import { SIGNATURE_REQUIREMENT_EVENT } from "../utils";

import { payerSignatureRenderCardDescription } from "./payerSignatureRenderCardDescription";

export const usePayerSignatureRequirementConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  RequirementTableItemData,
  ContextItem,
  SignatureRequest | undefined
> => {
  const [signSignatureRequest] = useSignSignatureRequest();
  const downloadVaultFile = useDownloadVaultFile();

  // v3 keeps a single fully executed copy on the request — the payer's where a
  // payer signature exists, otherwise the payee's. v1 minted a separate member
  // and client file and each side downloaded its own.
  const handleRequirementDownload = async (
    requirement: RequirementTableItemData,
    completedFileId: string
  ) => {
    if (await downloadVaultFile(completedFileId)) {
      callback(SIGNATURE_REQUIREMENT_EVENT.DownloadAsPayee, requirement);
    }
  };

  const handleRequirementSign = async (
    requirement: RequirementTableItemData,
    signatureRequest: SignatureRequest
  ) => {
    await signSignatureRequest(signatureRequest, {
      onSuccess() {
        callback(SIGNATURE_REQUIREMENT_EVENT.SignAsPayee, requirement);
      }
    });
  };

  const getDownloadAction = (
    requirement: RequirementTableItemData,
    context: ContextItem,
    signatureRequest: SignatureRequest | undefined
  ) => {
    // v1 allowed the download once the document was Complete or Pending
    // (signed, awaiting countersign) — the latter is `AwaitingCountersignature` on v3
    const isDownloadable =
      signatureRequest?.status === SignatureRequestStatus.COMPLETED ||
      signatureRequest?.status ===
        SignatureRequestStatus.AWAITING_COUNTERSIGNATURE;
    const completedFileId = signatureRequest?.completedFileId;

    if (completedFileId && isDownloadable) {
      return async () => {
        await handleRequirementDownload(requirement, completedFileId);
      };
    }

    return undefined;
  };

  const getSignAction = (
    requirement: RequirementTableItemData,
    context: ContextItem,
    signatureRequest: SignatureRequest | undefined
  ) => {
    if (
      selectorIsRequirementReadyToSignAsPayee(requirement, signatureRequest)
    ) {
      return async () => {
        await handleRequirementSign(requirement, signatureRequest!);
      };
    }
  };

  return {
    isActionRequired: (requirement, context, signatureRequest) => {
      return selectorIsRequirementReadyToSignAsPayee(
        requirement,
        signatureRequest
      );
    },
    requirementStatusAlert: (requirement, context, signatureRequest) => {
      if (
        selectorIsRequirementReadyToSignAsPayee(requirement, signatureRequest)
      ) {
        return {
          theme: "warning",
          title: "Action required",
          children:
            "Complete the requirement in order to be eligible to receive payments from your client.",
          icon: "alert"
        };
      }
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      signatureRequest
    ) => {
      return signatureRequest
        ? getSignatureRequestProgressSteps(
            signatureRequest,
            getHasPayerSigner(signatureRequest)
          )
        : undefined;
    },
    requirementName: getSignatureRequirementName,
    requirementDetails: (requirement, context, signatureRequest) => {
      const downloadAction = getDownloadAction(
        requirement,
        context,
        signatureRequest
      );

      const signAction = getSignAction(requirement, context, signatureRequest);

      return {
        header: {
          label: {
            text: getSignatureRequirementName(
              requirement,
              context,
              signatureRequest
            )
          }
        },
        children: payerSignatureRenderCardDescription(
          requirement,
          signatureRequest
        ),
        data: [],
        actions:
          downloadAction || signAction
            ? {
                alignment: "fill",
                buttons: [
                  {
                    label: "Download file",
                    onAsyncClick: downloadAction,
                    kind: "Secondary",
                    visible: !!downloadAction
                  },
                  {
                    label: "Start",
                    onAsyncClick: signAction,
                    kind: "Primary",
                    visible: !!signAction
                  }
                ]
              }
            : undefined
      };
    },
    toolbarActions: (requirement, context, signatureRequest) => {
      const actions: RequirementAction[] = [];

      const downloadAction = getDownloadAction(
        requirement,
        context,
        signatureRequest
      );

      const signAction = getSignAction(requirement, context, signatureRequest);

      if (signAction) {
        actions.push({
          label: "Sign document",
          onAsyncClick: signAction,
          buttonKind: "Primary",
          icon: "edit-line",
          buttonIcon: null,
          mainAction: true
        });
      }

      if (downloadAction) {
        actions.push({
          onAsyncClick: downloadAction,
          label: "Download file",
          buttonKind: signAction ? undefined : "Secondary",
          icon: "download",
          buttonIcon: null
        });
      }

      return actions;
    },
    nameCell: (requirement, context, signatureRequest) => {
      return {
        text: getSignatureRequirementName(
          requirement,
          context,
          signatureRequest
        )
      };
    },
    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.type
      }),
    statusCell: (requirement, context, signatureRequest) => {
      return getSignaturePayeeRequirementStatus(
        requirement,
        context,
        signatureRequest
      );
    }
  };
};
